// services/api.js
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getHeaders = () => ({
  'Content-Type': 'application/json',
});

// Helper to handle responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `API error: ${response.status}`);
  }
  return response.json();
};

export const fetchFilterOptionsCombined = async (userId, filterType, combination = null, signal) => {
 
  if (filterType === 'priority') {
    return [
      { priorityValue: 1, value: 1, label: "No Priority" },
      { priorityValue: 3, value: 3, label: "High" },
      { priorityValue: 4, value: 4, label: "Medium" },
      { priorityValue: 5, value: 5, label: "Completed" }
    ];
  }

  let endpoint = '';
  let responseField = 'jobs';

  switch (filterType) {
    case 'date':
      endpoint = `${API_BASE_URL}/api/Filters/GetFilteredSiteNoteDate/${userId}`;
      break;
    case 'workspace':
      endpoint = `${API_BASE_URL}/api/Filters/GetFilteredWorkspace/${userId}`;
      break;
    case 'project':
      endpoint = `${API_BASE_URL}/api/Filters/GetFilteredProject/${userId}`;
      break;
    case 'job':
      endpoint = `${API_BASE_URL}/api/Filters/GetFilteredJob/${userId}`;
      break;
    case 'task':
      endpoint = `${API_BASE_URL}/api/Filters/GetFilteredTask/${userId}`;
      responseField = 'tasks';
      break;
    case 'userName':
      endpoint = `${API_BASE_URL}/api/Filters/GetFilteredSiteNoteUser/${userId}`;
      break;
    case 'jobManager':
      endpoint = `${API_BASE_URL}/api/Filters/GetFilteredJobManager/${userId}`;
      responseField = 'managers';
      break;
    default:
      return [];
  }

  let url = endpoint;
  if (combination) {
    const queryParams = new URLSearchParams();
    if (combination.workspace) queryParams.append('WorkspaceId', combination.workspace);
    if (combination.project) queryParams.append('ProjectId', combination.project);
    if (combination.job) queryParams.append('JobId', combination.job);
    if (combination.date) queryParams.append('NoteDate', combination.date);
    if (combination.task) queryParams.append('TaskId', combination.task);
    if (combination.userName) queryParams.append('UserId', combination.userName);
    if (combination.priority) queryParams.append('Priority', combination.priority);
    if (combination.jobManager) queryParams.append('JobManagerId', combination.jobManager);
    
    if (queryParams.toString()) {
      url = `${endpoint}?${queryParams.toString()}`;
    }
  }

  const response = await fetch(url, { signal });
  const data = await handleResponse(response);
  return data[responseField] || [];
};

export const fetchSimpleOptions = fetchFilterOptionsCombined;
export const fetchFilteredOptions = fetchFilterOptionsCombined;


// ========== Stacked Jobs ==========
export const fetchStackedJobsFromApi = async (userId, apiUrl) => {
  const url = `${apiUrl}/SiteNote/GetStackedJobs?userId=${userId}&pageNumber=1&pageSize=50`;
  const response = await fetch(url);
  const data = await handleResponse(response);
  if (!data.jobs || !Array.isArray(data.jobs)) return [];

  return data.jobs.map(job => {
    return {
      jobId: job.jobId || job.id || job.jobID || job.Id || job.JobId || "",
      jobName: job.jobName || job.name || job.job || "",
      jobDescription: job.jobDescription || job.description || job.Name || "",
      projectId: job.projectId || job.project || "",
      projectName: job.projectName || job.projectName || job.project || "",
      workspaceId: job.workspaceId || job.workspace?.id || job.WorkspaceId || "",
      workspaceName: job.workspaceName || job.workspace || "",
      noteCount: job.totalSiteNotes || job.matchingNotesCount || 0,
      latestTimeStamp: job.latestTimeStamp || job.latestMatchingNote,
      lastSiteNoteUserName: job.lastSiteNoteUserName || null,
      lastSiteNote: job.lastSiteNote || "",
      jobFriendlyId: job.jobFriendlyId || "",
      isLoadingNotes: false,
      hasLoadedNotes: false,
      notes: null,
      errorLoadingNotes: null,
      totalTasks: job.totalTasks || 0
    };
  });
};

export const fetchStackedJobsWithSearch = async (userId, searchText, apiUrl) => {
  const params = new URLSearchParams({ searchText, userId });
  const url = `${apiUrl}/SiteNote/GetJobsBySiteNoteText?${params.toString()}`;
  const response = await fetch(url);
  const data = await handleResponse(response);
  return (data.jobs || []).map(job => {
    return {
      jobId: job.jobId || job.id || job.jobID || job.Id || job.JobId || "",
      jobName: job.jobName || job.name || job.job || "",
      jobDescription: job.jobDescription || job.description || job.Name || "",
      noteCount: job.matchingNotesCount || job.totalSiteNotes || 0,
      latestTimeStamp: job.latestMatchingNote || job.latestTimeStamp,
      workspaceName: job.workspaceName || job.workspace || "",
      projectName: job.projectName || job.project || "",
      projectId: job.projectId || job.project || "",
      friendlyId:
        job.friendlyId || job.FriendlyId || job.jobFriendlyId || job.jobFriendlyID || job.job?.friendlyId || "",
      isLoadingNotes: false,
      hasLoadedNotes: false,
      notes: null,
      errorLoadingNotes: null,
    };
  });
};

// ========== Tasks and Notes by Task ==========
export const fetchTasksForJobApi = async (jobId, apiUrl) => {
  const response = await fetch(`${apiUrl}/JobTasks/job/${jobId}`);
  const data = await handleResponse(response);
  return data.success && data.data ? data.data : [];
};

export const fetchNotesForTaskApi = async (userId, taskId, page = 1, pageSize = 20, apiUrl) => {
  const params = new URLSearchParams({
    pageNumber: page.toString(),
    pageSize: pageSize.toString(),
    taskId,
    userId,
  });
  const url = `${apiUrl}/SiteNote/GetSiteNotesWithFilters?${params.toString()}`;
  const response = await fetch(url);
  const data = await handleResponse(response);
  const notesArray = data.data || data.siteNotes || [];
  const totalCount = data.totalCount || data.total || data.totalRecords || notesArray.length;
  const hasMore = (page - 1) * pageSize + notesArray.length < totalCount;

  const formattedNotes = notesArray.map(n => ({
    ...n,
    id: n.id,
    userName: n.userName || n.UserName || 'Unknown',
    documentCount: n.documentCount || 0,
    timeStamp: n.timeStamp || n.date,
    date: n.date || n.createdDate,
    workspace: n.workspace || '',
    project: n.project || '',
    job: n.job || '',
    note: n.note || n.content,
    jobId: n.jobId,
    taskId: n.taskId,
    priority: n.priority || 1,
  }));

  return { notes: formattedNotes, totalCount, hasMore };
};

// ========== Inline Images ==========
export const fetchInlineImagesApi = async (noteId, apiUrl) => {
  const response = await fetch(`${apiUrl}/InlineImages/GetInlineImagesBySiteNote?siteNoteId=${noteId}`);
  if (!response.ok) return [];
  const data = await response.json();
  return data.images || [];
};

export const deleteInlineImagesApi = async (noteId, apiUrl) => {
  const images = await fetchInlineImagesApi(noteId, apiUrl);
  for (const image of images) {
    await fetch(`${apiUrl}/InlineImages/DeleteInlineImage/${image.id}`, { method: 'DELETE' });
  }
  return true;
};

// ========== Attached Documents ==========
export const fetchAttachedDocumentsApi = async (noteId, apiUrl) => {
  const response = await fetch(`${apiUrl}/Documents/GetDocumentMetadataByReference?siteNoteId=${noteId}`);
  if (!response.ok) return [];
  const data = await response.json();
  return data.documents || [];
};

export const deleteAttachedDocumentsApi = async (noteId, apiUrl) => {
  const docs = await fetchAttachedDocumentsApi(noteId, apiUrl);
  for (const doc of docs) {
    await fetch(`${apiUrl}/Documents/DeleteDocument/${doc.id}`, { method: 'DELETE' });
  }
  return true;
};

// ========== Delete Note ==========
export const deleteNoteApi = async (noteId, userId, apiUrl) => {
  const url = new URL(`${apiUrl}/SiteNote/DeleteSiteNote/${noteId}`);
  url.searchParams.append('userId', userId);
  const response = await fetch(url.toString(), { method: 'DELETE', headers: getHeaders() });
  return handleResponse(response);
};

// ========== User and Workspace ==========
export const fetchCurrentUserApi = async (userId, apiUrl) => {
  const response = await fetch(`${apiUrl}/UserManagement/GetUserById/${userId}`);
  if (!response.ok) return null;
  const data = await response.json();
  return data.user;
};

export const fetchWorkspacesByUserIdApi = async (userId, apiUrl) => {
  const response = await fetch(`${apiUrl}/Workspace/GetActiveWorkspacesNameByUserId/${userId}`);
  if (!response.ok) return [];
  const data = await response.json();
  return data.workspaces || data || [];
};

export const fetchWorkspaceRoleApi = async (userId, workspaceId, apiUrl) => {
  const response = await fetch(`${apiUrl}/Workspace/GetWorkspacesByUserId/${userId}`);
  const data = await handleResponse(response);
  const workspaces = data.workspaces || [];
  const defaultWorkspace = workspaces.find(w => (w.id || w.workspaceId)?.toString() === workspaceId?.toString());
  return defaultWorkspace?.role || null;
};